<script setup lang="ts">
import { useI18n } from 'vue-i18n'
const { t } = useI18n()
import { Button as KButton } from '@progress/kendo-vue-buttons'
import {
  ListView,
  ListViewHeader,
} from '@progress/kendo-vue-listview';
import { formatDateWithShortMonth } from '@/api/Common';
import { useLogger } from 'vue-logger-plugin'
import { ref, onMounted, watch, computed } from 'vue'
import { setStoreLogger } from '@/logger.ts'
import AoeConsultantLocatorDetail from './AoeConsultantLocatorDetail.vue'
import AoeHostDetail from './AoeHostDetail.vue'
import type { IEventHost, IEventHostResponse } from '@/types/EventTypes'
import type { FilterContainer } from '@/types/FilterTypes'
import { EventAddToCart, PayeeAddToCart } from '@/services/CartService';
import type { IConsultantLocator } from '@/types/LocatorTypes'
import { GetLocatorConsultants, GetLocatorConsultantsByZip,GetLocatorConsultantsKnowAll } from '@/services/LocatorService'
import { GetEventHosts } from '@/services/EventService'
import {
  GetCountryCode,GetBeeNumberSite,GetRootUrl,GetHostBenefitsUrl,GetHostPortalUrl,GetMyStoryUrl
} from '@/stores/SettingsStore.ts'
import { GetConsultantFromLocalStorageOrApi } from '@/services/ConsultantService'
import { trackConsultantSelected } from '@/analytics/index'
import { AutoComplete, type AutoCompleteChangeEvent, DropDownList } from '@progress/kendo-vue-dropdowns'
import { TextBox as KTextBox, RadioButton } from '@progress/kendo-vue-inputs'
import type { NumericDropdownOption } from '@/types/DropdownTypes'
import type { DropdownOption } from '@/types/DropdownTypes'
import type {
  IAoeCart
} from '@/types/CartTypes'
import { SetIsLoading } from '@/services/AoeHelpers.ts'
import { searchIcon } from '@progress/kendo-svg-icons'
import { useCookies } from 'vue3-cookies'


const logger = useLogger()
setStoreLogger(logger)
logger.debug('AOECartDrawer.vue')

const countryCode = ref<string>('')
const selectedCountry = ref<string>('')
const locatorConsultants = ref<IConsultantLocator[]>([])
const locationConsultants = ref<IConsultantLocator[]>([])
const selectedEventId = ref<number>(0)
const selectedConsultant = ref<number>(0)
const consultantBusinessname = ref<string>('')
const searchText = ref<string>('')
const consultantByName = ref(true)
const consultantByLocation = ref(false)
const txtZipCode = ref<string>('')
const hostBenefitsUrl = ref<string>('')
const hasSearched = ref(false)
const searchRadius = ref<number>(25)
const { cookies } = useCookies()

const radiusOptions: NumericDropdownOption[] = [
  { value: 5 },
  { value: 25 },
  { value: 50 },
  { value: 100 }
]

const countryOptions: DropdownOption[] = [
  { value: 'DE', labelKey: 'countries.germany' },
  { value: 'AT', labelKey: 'countries.austria' },
  { value: 'FR', labelKey: 'countries.france' }
]

const countryOptionsWithLabels = computed(() => {
  return countryOptions.map((opt: DropdownOption) => ({
    value: opt.value,
    label: t(opt.labelKey)
  }))
})

const props = defineProps<{
  cart: IAoeCart | null
  ispopover: boolean
  isPartyOnly: boolean
  eventHosts: IEventHost[]
}>()

const emit = defineEmits(["closePopup"]);

onMounted(async () => {
  // Initialize hostBenefitsUrl
  hostBenefitsUrl.value = await GetHostBenefitsUrl();

  // Initialize country code from site
  const siteCountryCode = await GetCountryCode();
  countryCode.value = siteCountryCode;
  selectedCountry.value = siteCountryCode;
});


async function selectedHostHandler(event: IEventHost)
{
  logger.debug('selectedHostHandler', event)
  selectedEventId.value = event.eventId

  // Get the host portal URL from settings
  const hostPortalUrl = await GetHostPortalUrl()

  // Construct the URL with the eventId as a query parameter
  const redirectUrl = `${hostPortalUrl}?eventId=${event.eventId}`

  // Redirect to the host portal
  logger.debug('Redirecting to host portal:', redirectUrl)
  window.location.href = redirectUrl
}

function selectedConsultantHandler(event: IConsultantLocator, location?: boolean)
{
  logger.debug('selectedConsultantHandler', event)
  selectedConsultant.value = event.beeNumber
  consultantBusinessname.value = event.businessName || ''

  if(location){
    btnFindPartyConfirmHandler()
  }
}

function clearTextHandler(event: AutoCompleteChangeEvent)
{
  if (event.event != undefined && event.event.currentTarget.className === "k-clear-value")
  {
    logger.debug('clearTextHandler')
    selectedEventId.value = 0
  }
}
// Create a ref to store the search results
const searchResults = ref<IEventHost[]>([])

function changeHostTextHandler(event: AutoCompleteChangeEvent) {
  if (event.event != undefined && event.event.currentTarget.className === "k-clear-value") {
    logger.debug('clearHostTextHandler')
    selectedEventId.value = 0
    searchResults.value = []
  } else {
    if (event.target.value.length >= 3) {
      const searchText = event.target.value;

      SetIsLoading(true)
      searchResults.value = []

      // Create a filter container for server-side filtering
      // Try different field name variations to see which one works
      const filterContainer: FilterContainer = {
        Where: {
          OperatorType: "Or",
          Operands: [
            {
              Field: "FirstName",
              Value: searchText.toUpperCase(),
              FilterType: "Contains"
            },
            {
              Field: "LastName",
              Value: searchText.toUpperCase(),
              FilterType: "Contains"
            }
          ]
        }
      };

      // Create the request object - try two different approaches
      // Approach 1: Using responseFilter
      const request = {
        responseFilter: filterContainer
      };





      // Call GetEventHosts with server-side filtering
      GetEventHosts(request).then((eventHostResponse: IEventHostResponse) => {
        logger.debug('Event host search results:', eventHostResponse)

        // Process the results
        if (eventHostResponse && eventHostResponse.items) {
          // Map the hosts to include all required fields for the AutoComplete template
          const events: IEventHost[] = eventHostResponse.items.map(host => {
            // Create a hostName from firstName and lastName
            const hostName = `${host.firstName || ''} ${host.lastName || ''}`.trim() || 'Unknown Host';

            // Add event city if available for better identification
            const displayLocation = host.eventCity ? ` (${host.eventCity})` : '';

            // Format the date for display
            const eventDate = host.eventDate ? formatDateWithShortMonth(host.eventDate) : '';

            // Create a default phone number object that matches IPhoneOutput
            const defaultPhoneNumber = {
              number: '',
              codeIdPhoneType: '',
              countryCode: '',
              formattedNumber: '',
              extension: null
            };

            return {
              ...host,
              hostName: `${hostName}${displayLocation}`,
              eventDate: eventDate,
              aoeCartIdToken: props.cart?.aoeCartIdToken || '',
              email: host.email || '',
              phoneNumber: host.phoneNumber || defaultPhoneNumber
            }
          });

          // Update the search results
          searchResults.value = events
          logger.debug('Processed events:', events)
        }
      }).catch((error: Error) => {
        logger.error('Error searching for events:', error)
      }).finally(() => {
        SetIsLoading(false)
      });
    }
  }
}


const latestConsultantSearchRequestId = ref(0)

async function performConsultantSearch(searchValue: string)
{
  const requestId = ++latestConsultantSearchRequestId.value

  SetIsLoading(true)
  try {
    // Check if search value contains a space
    if (searchValue.indexOf(' ') > 0) {
      // Split by space - first part is firstName, second part is lastName
      const names = searchValue.split(' ')
      const firstName = names[0]
      const lastName = names.slice(1).join(' ') // Handle multiple spaces/names

      logger.debug('Searching consultants by full name', { firstName, lastName })

      // Make a single API call with both firstName and lastName
      const response = await GetLocatorConsultantsKnowAll({
        firstName: firstName,
        lastName: lastName,
        includePublicStoreDisabled: true
      })

      logger.debug('GetLocatorConsultants response', { response })

      if (requestId !== latestConsultantSearchRequestId.value) {
        logger.debug('Ignoring stale consultant search response', requestId)
        return
      }

      // Add aoeCartIdToken to each consultant and replace (never accumulate) the list
      const consultants = response?.items || []
      locatorConsultants.value = consultants.map(consultant => {
        const eH = consultant as IConsultantLocator
        eH.aoeCartIdToken = props.cart?.aoeCartIdToken || ''
        return eH
      })
    } else {
      // No space - search by first name OR last name
      logger.debug('Searching consultants by first name and last name', { searchValue })

      // Make two parallel API calls - one for first name, one for last name
      const [firstNameResponse, lastNameResponse] = await Promise.all([
        GetLocatorConsultantsKnowAll({
          firstName: searchValue,
          lastName: null,
          includePublicStoreDisabled: true
        }),
        GetLocatorConsultantsKnowAll({
          firstName: null,
          lastName: searchValue,
          includePublicStoreDisabled: true
        })
      ])

      logger.debug('GetLocatorConsultants responses', { firstNameResponse, lastNameResponse })

      if (requestId !== latestConsultantSearchRequestId.value) {
        logger.debug('Ignoring stale consultant search response', requestId)
        return
      }

      // Combine results from both searches
      const firstNameResults = firstNameResponse?.items || []
      const lastNameResults = lastNameResponse?.items || []

      // Create a Map to deduplicate by beeNumber
      const consultantMap = new Map<number, IConsultantLocator>()

      // Add all first name results
      firstNameResults.forEach(consultant => {
        const eH = consultant as IConsultantLocator
        eH.aoeCartIdToken = props.cart?.aoeCartIdToken || ''
        consultantMap.set(consultant.beeNumber, eH)
      })

      // Add all last name results (will not duplicate if beeNumber already exists)
      lastNameResults.forEach(consultant => {
        const eH = consultant as IConsultantLocator
        eH.aoeCartIdToken = props.cart?.aoeCartIdToken || ''
        consultantMap.set(consultant.beeNumber, eH)
      })

      // Convert Map back to array - replaces (never accumulates) the list
      locatorConsultants.value = Array.from(consultantMap.values())

      logger.debug('Combined and deduplicated consultants', {
        firstNameCount: firstNameResults.length,
        lastNameCount: lastNameResults.length,
        totalUnique: locatorConsultants.value.length,
        consultants: locatorConsultants.value
      })
    }
  } catch (error) {
    console.error('Error fetching consultants:', error)
    if (requestId === latestConsultantSearchRequestId.value) {
      locatorConsultants.value = []
    }
  } finally {
    if (requestId === latestConsultantSearchRequestId.value) {
      SetIsLoading(false)
    }
  }
}

const debouncedPerformConsultantSearch = debounce((searchValue: string) => {
  void performConsultantSearch(searchValue)
}, 300)

function handleConsultantSelectionChange(event: AutoCompleteChangeEvent)
{
  if (event.event != undefined && event.event.currentTarget.className === "k-clear-value")
  {
    logger.debug('clearConsultantTextHandler')
    selectedConsultant.value = 0
    locatorConsultants.value = []
    searchText.value = ''
    latestConsultantSearchRequestId.value += 1
  }
  else
  {
    // Check if a consultant was selected from the dropdown
    if (event.value && typeof event.value === 'object' && 'beeNumber' in event.value) {
      logger.debug('Consultant selected from dropdown', event.value)
      const consultant = event.value as IConsultantLocator
      selectedConsultant.value = consultant.beeNumber
      consultantBusinessname.value = consultant.businessName || ''
      return
    }

    // Otherwise, handle text input for search
    searchText.value = event.target.value || ''

    if (event.target.value.length >= 3)
    {
      debouncedPerformConsultantSearch(event.target.value)
    } else {
      latestConsultantSearchRequestId.value += 1
      locatorConsultants.value = []
    }
  }
}

function radioBtnChangeNameHandler()
{
  logger.debug('radioBtnChangeNameHandler')
  consultantByName.value = true
  consultantByLocation.value = false
}

function radioBtnChangeLocationHandler()
{
  logger.debug('radioBtnChangeLocationHandler')
  consultantByName.value = false
  consultantByLocation.value = true
}

async function btnFindPartyConfirmHandler()
{
  logger.debug('btnFindPartyConfirmHandler')

  emit('closePopup')

  trackConsultantSelected(consultantBusinessname.value, selectedConsultant.value)

  const consultant = await GetConsultantFromLocalStorageOrApi(selectedConsultant.value)

  if (consultant?.publicStoreDisabled) {
    logger.debug('btnFindPartyConfirmHandler', 'consultant PWS disabled, redirecting to My Story', selectedConsultant.value)
    const myStoryUrl = await GetMyStoryUrl()
    const targetUrl = `${myStoryUrl}?beenumber=${encodeURIComponent(selectedConsultant.value)}`
    logger.debug('btnFindPartyConfirmHandler','Redirecting to:', targetUrl);
    window.location.href = targetUrl
  }
  else {
    cookies.set('ReplicatedSiteBeeNumber', selectedConsultant.value.toString());
    const rootUrl = await GetRootUrl();
    const targetRootUrl = rootUrl && rootUrl.length > 0 ? rootUrl : '/'
    logger.debug('btnFindPartyConfirmHandler','Redirecting to:', targetRootUrl);
    window.location.href = targetRootUrl;
  }
}

async function toReplicatedSite()
{
  if (consultantBusinessname.value)
  {
    // Get the base URL (protocol + host + port)
    const baseUrl = `${window.location.protocol}//${window.location.host}`;

    // Get the root URL from settings (e.g., "/ecomm/")
    const rootUrl = await GetRootUrl();
    const safeRootUrl = rootUrl && rootUrl.length > 0 ? rootUrl : '/'

    // Remove any trailing slashes from rootUrl for consistency
    const cleanRootUrl = safeRootUrl.replace(/\/+$/, '');

    // Construct the final URL: baseUrl + /pws/consultantBusinessname + rootUrl
    const finalUrl = `${baseUrl}/pws/${consultantBusinessname.value}${cleanRootUrl}/`;

    logger.debug('Redirecting to:', finalUrl);
    window.location.href = finalUrl;
  }
  else
  {
    logger.error('Bee number site is not available');
  }
}

function resetForm() {
  selectedConsultant.value = 0
  consultantBusinessname.value = ''
  searchText.value = ''
  selectedEventId.value = 0
  locatorConsultants.value = []
  locationConsultants.value = []
  txtZipCode.value = ''
  hasSearched.value = false
  consultantByName.value = true
  consultantByLocation.value = false
  searchResults.value = []
}

function closeCheckoutPopupHandler()
{
  logger.debug('closeCheckoutPopupHandler')
  resetForm()
  emit('closePopup')
}

watch(txtZipCode, (newVal) => {
  if (!newVal || newVal.trim().length === 0) {
    locationConsultants.value = []
    hasSearched.value = false
  }
})

function handleRadiusChange(e: any) {
  const newValue = e.value?.value !== undefined ? e.value.value : e.value
  searchRadius.value = Number(newValue)
  if (txtZipCode.value) {
    searchClickHandler()
  }
}

async function searchClickHandler() {
  logger.debug('searchClickHandler')

  SetIsLoading(true)
  hasSearched.value = false
  locationConsultants.value = []

  const zip = txtZipCode.value

  GetLocatorConsultantsByZip({
    postalCode: zip,
    distance: searchRadius.value,
    countryCode: selectedCountry.value,
    includePublicStoreDisabled: true
  }).then((locConsultantResp) => {
    for (const locCon of locConsultantResp.items) {
      const eH = locCon as IConsultantLocator
      eH.aoeCartIdToken = props.cart?.aoeCartIdToken || ''
      locationConsultants.value.push(eH)
    }

    hasSearched.value = true
    logger.debug('locationConsultants', locationConsultants)
  }).finally(() => {
    SetIsLoading(false)
  })
}

// Simple debounce utility for a single-string-argument function
function debounce(func: (value: string) => void, wait: number) {
  let timeout: ReturnType<typeof setTimeout> | null = null
  return function (value: string) {
    if (timeout) clearTimeout(timeout)
    timeout = setTimeout(() => func(value), wait)
  }
}
</script>

<template>
  <div class="row mt-3">
        <div v-if="!isPartyOnly" class="col-12 popOverGuest">
          <p class="pageHeader">{{ t('cart.FindConsultatHeader') }}</p>
          <p class="pageSubSubHeader">{{ t('cart.FindConsultatSubHeader') }}</p>
          <div class="row locator-radio-row">
            <div class="col-6">
              <RadioButton
                :name="'group1'"
                size="large"
                :checked="consultantByName"
                @change="radioBtnChangeNameHandler"
                >{{ t('cart.ConsultantByName') }}
              </RadioButton>
            </div>
            <div class="col-6">
              <RadioButton
                :name="'group1'"
                size="large"
                :checked="consultantByLocation"
                @change="radioBtnChangeLocationHandler"
                >{{ t('cart.ConsultantByLocation') }}
              </RadioButton>
            </div>
          </div>
          <div v-if="consultantByName" class="row mt-3">
            <div class="col-12">
              <AutoComplete class="consultantAutoComplete"
                :data-items="locatorConsultants"
                :placeholder="t('cart.PlaceHolderConsultantName')"
                :text-field="'name'"
                :filterable="false"
                :value="searchText"
                :item-render="AoeConsultantLocatorDetail"
                @selected-consultant="selectedConsultantHandler"
                @change="handleConsultantSelectionChange"
              >
                <template v-slot:myTemplate="{ props }">
                  <custom
                    :style="props.style"
                    :class="props.itemClass"
                    :data-item="props.dataItem"
                    :focused="props.focused"
                    :index="props.index"
                    />
                </template>
              </AutoComplete>
            </div>
          </div>
          <div v-if="consultantByLocation" class="row mt-3">
            <div class="col-12">
              <div class="locator-search-bar">
                <DropDownList
                  :data-items="countryOptionsWithLabels"
                  :text-field="'label'"
                  :data-item-key="'value'"
                  :value="countryOptionsWithLabels.find((opt: any) => opt.value === selectedCountry)"
                  @change="(e: any) => selectedCountry = e.value.value"
                  class="country-dropdown"
                />
                <KTextBox
                    :placeholder="t('cart.PlaceholderZipcode')"
                    v-model="txtZipCode"
                    :rounded="'large'"
                    :theme-color="'primary'"
                    :show-clear-button="true"
                    @keyup.enter="searchClickHandler"
                  ></KTextBox>
                <KButton
                  class="locator-search-button"
                  :svg-icon="searchIcon"
                  :fill-mode="'clear'"
                  @click="searchClickHandler"
                ></KButton>
              </div>
              <div class="radius-selector-container">
                <label class="radius-label">{{ t('consultantLocator.searchRadius') }}</label>
                <DropDownList
                  :data-items="radiusOptions"
                  :text-field="'value'"
                  :data-item-key="'value'"
                  :value="radiusOptions.find(opt => opt.value === searchRadius)"
                  @change="handleRadiusChange"
                  class="radius-dropdown"
                />
                <span class="radius-unit">{{ t('consultantLocator.kilometers') }}</span>
              </div>
              <div v-if="hasSearched && locationConsultants.length === 0" class="row mt-3">
                <div class="col-12">
                  <div class="no-results-message">
                    {{ t('consultantLocator.noConsultantsFound') }}
                  </div>
                </div>
              </div>
              <div v-if="locationConsultants.length > 0" class="row mt-3">
                <div class="col-12">
                  <ListView
                    :style="{ width: '100%', height: '300px' }"
                    :data-items="locationConsultants"
                    :item="'myItem'"
                    :header="'myHeader'"
                  >
                    <template v-slot:myItem="{ props }">
                      <div class="locatorItem mt-2" :class="props.dataItem.beeNumber == selectedConsultant ? 'selectedConsultant' : ''">
                        <div class="locatorItemCell">
                            <img v-if="props.dataItem.photoUrl && props.dataItem.showPhoto !== false"
                              :src="props.dataItem.photoUrl"
                              :alt="t('cart.ConsultantPhotoAlt')"
                              class="locatorPhoto">
                        </div>
                        <div class="locatorItemCell">
                          <div class="row" v-if="props.dataItem.showName !== false">
                            <div class="col-12 font-weight-bold">
                                  {{ props.dataItem.name }}
                            </div>
                          </div>
                          <div class="row" v-if="props.dataItem.showLocation !== false">
                            <div class="col-12">
                                  {{ props.dataItem.city }}, {{ props.dataItem.region }}
                            </div>
                          </div>
                          <div class="row" v-if="props.dataItem.emailAddress && props.dataItem.showEmail !== false">
                            <div class="col-12">
                              ✉ {{ props.dataItem.emailAddress }}
                            </div>
                          </div>
                          <div class="row" v-if="(props.dataItem.mobilePhone && props.dataItem.showMobile !== false) || (props.dataItem.landline && props.dataItem.showLandline !== false)">
                            <div class="col-12">
                              ☎
                              <span v-if="props.dataItem.mobilePhone && props.dataItem.showMobile !== false" class="mr-2">{{ props.dataItem.mobilePhone }} (M)</span>
                              <span v-if="props.dataItem.landline && props.dataItem.showLandline !== false">{{ props.dataItem.landline }} (L)</span>
                            </div>
                          </div>
                        </div>
                        <div class="locatorItemCell text-center locatorItemCellButton">
                          <KButton
                          class="btnLocatorSelect"
                          :rounded="'large'"
                          :theme-color="'btnLocatorSelect'"
                            @click="selectedConsultantHandler(props.dataItem, true)"
                            >{{ t('cart.SelectButton') }}</KButton>
                        </div>
                      </div>
                    </template>
                    <template v-slot:myHeader>
                      <ListViewHeader
                        :style="{ color: '#a0a0a0', 'font-size': '14px' }"
                        class="pl-3 pb-2 pt-2"
                      >
                        {{ t('cart.ConsultantListHeader') }}
                      </ListViewHeader>
                    </template>
                  </ListView>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="row mt-3 locator-action-row">
        <div class="col-6 locator-action-spacer"></div>
        <div class="col-3 btnFindPartyCancelDiv">
          <KButton
            id="btnFindPartyCancel"
            ref="btnFindPartyCancel"
            class="btnFindPartyCancel"
            :rounded="'large'"
            :theme-color="'btnFindPartyCancel'"
            @click="closeCheckoutPopupHandler"
            >{{ t('labels.Cancel') }}</KButton>
        </div>
        <div class="col-3 btnFindPartyConfirmDiv">
          <KButton
            id="btnFindPartyConfirm"
            ref="btnFindPartyConfirm"
            class="btnFindPartyConfirm"
            :rounded="'large'"
            :theme-color="'primary'"
            :disabled="selectedConsultant === 0"
            @click="btnFindPartyConfirmHandler()"
            >{{ t('labels.Confirm') }}</KButton>
        </div>
      </div>
</template>

<style lang="scss">

.locatorItem {
  display: grid;
  grid-template-columns: 25% 45% 25%;
  padding: $spacing-2-5;

  @include typography-small;

}

.locatorItem > div {
  padding: $spacing-1;
}

@media (max-width: 768px) {
  .locatorItem {
    grid-template-columns: 30% 70% 0%;
    padding: $spacing-1;
    word-wrap: break-word;

    img {
      max-width: none;
    }

    .locatorItemCellButton {
      grid-column: 1 / -1;
    }
  }
}

.no-results-message {
  text-align: center;
  padding: 1.5rem;
  margin: 1rem 0;
  background-color: #fff3cd;
  border: 1px solid #ffc107;
  @include typography-body;
  border-radius: $radius-md;
  color: #856404;
}

.locator-search-bar {
  display: flex;
  align-items: center;
  gap: $spacing-2;

  .k-textbox {
    flex: 1;
  }

  .country-dropdown {
    width: 120px;
    min-width: 120px;
  }
}

.locator-search-button {
  /* !important required: Override Kendo button defaults for larger search icon sizing */
  padding: $spacing-2 !important;
  flex-shrink: 0;

  .k-icon,
  .k-svg-icon {
    width: 24px;
    height: 24px;
  }
}

.btnFindPartyConfirm:disabled,
.btnFindPartyConfirm[disabled] {
  opacity: 0.5;
  cursor: not-allowed;
}

.locator-radio-row {
  margin-bottom: $spacing-2;

  .k-radio {
    margin-right: $spacing-2;
  }
}

.locatorItemCellButton {
  display: flex;
  justify-content: center;
  align-items: center;
}

@media (max-width: 575px) {
  .locator-action-spacer {
    display: none;
  }

  .locator-action-row {
    .btnFindPartyCancelDiv,
    .btnFindPartyConfirmDiv {
      flex: 0 0 50%;
      max-width: 50%;
    }
  }

  .btnFindPartyCancel,
  .btnFindPartyConfirm {
    width: 100%;
  }
}

.radius-selector-container {
  display: flex;
  align-items: center;
  gap: $spacing-2;
  margin-top: $spacing-4;
  margin-bottom: $spacing-2;
}

.radius-label {
  @include typography-body;
  font-weight: 500;
  color: #333;
  margin: 0;
  white-space: nowrap;
}

.radius-dropdown {
  width: 80px;
  min-width: 80px;
}

.radius-unit {
  @include typography-body;
  color: #666;
  margin-left: $spacing-1;
}
</style>
